import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, MARKET_META } from "../lib/api";
import { AdminShell } from "./AdminShell";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

const prettyDMY = (iso) => {
    if (!iso) return "";
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return `${d}-${m}-${y}`;
};

const AdminPreviousResults = () => {
    const { marketKey } = useParams();
    const navigate = useNavigate();
    const meta = MARKET_META[marketKey];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/results", { params: { market_key: marketKey, limit: 200 } });
            setRows(data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [marketKey]);

    const doDelete = async (id) => {
        if (!window.confirm("Yeh result delete karna hai?")) return;
        try {
            await api.delete(`/results/${id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Delete failed"); }
    };

    if (!meta) return <AdminShell title="Previous Results"><div>Market not found</div></AdminShell>;

    return (
        <AdminShell title="Previous Results">
            <div className="mb-3 rounded-md px-4 py-2 text-white text-sm font-semibold inline-flex items-center gap-2"
                 style={{ background: meta.accent, color: meta.headerTextColor || "#FFF" }}>
                <span className="text-lg">{meta.emoji}</span>
                {meta.name}
            </div>

            <h2 className="font-display font-bold text-2xl md:text-3xl text-blue-950 tracking-tight text-center uppercase">
                Previous Results
            </h2>

            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200" data-testid="admin-prev-table">
                <table className="w-full text-sm">
                    <thead className="bg-white">
                        <tr className="text-blue-900 font-semibold border-b border-slate-200">
                            <th className="px-3 py-3 text-center">Sl.No</th>
                            <th className="px-3 py-3 text-center">Date</th>
                            <th className="px-3 py-3 text-center">First Round</th>
                            <th className="px-3 py-3 text-center">Second Round</th>
                            <th className="px-3 py-3 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="py-6 text-center text-slate-400">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-slate-400" data-testid="admin-prev-empty">
                                    Koi previous result nahi hai. Naya result publish kar sakte hain.
                                </td>
                            </tr>
                        ) : rows.map((r, i) => (
                            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-prev-row-${r.id}`}>
                                <td className="px-3 py-3 text-center text-slate-700">{i + 1}</td>
                                <td className="px-3 py-3 text-center text-slate-700 font-num">{prettyDMY(r.date)}</td>
                                <td className="px-3 py-3 text-center text-blue-900 result-digit text-lg">
                                    {r.fr === null || r.fr === undefined || r.fr === "" ? "—" : String(r.fr).padStart(2, "0")}
                                </td>
                                <td className="px-3 py-3 text-center text-blue-900 result-digit text-lg">
                                    {r.sr === null || r.sr === undefined || r.sr === "" ? "—" : String(r.sr).padStart(2, "0")}
                                </td>
                                <td className="px-3 py-3 text-center">
                                    <div className="inline-flex gap-2">
                                        <button
                                            onClick={() => navigate(`/admin/results/${marketKey}?date=${r.date}`)}
                                            data-testid={`admin-prev-edit-${r.id}`}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                                        >
                                            <Pencil className="h-3 w-3" /> Edit
                                        </button>
                                        <button
                                            onClick={() => doDelete(r.id)}
                                            data-testid={`admin-prev-delete-${r.id}`}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-red-500 text-white text-xs font-semibold hover:bg-red-600"
                                        >
                                            <Trash2 className="h-3 w-3" /> Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </AdminShell>
    );
};

export default AdminPreviousResults;
